import { FormControl, MenuItem, Stack, TextField, Select, Checkbox, FormControlLabel, InputLabel, FormGroup, Card, Divider, Button, ButtonGroup, Paper, Typography, Tabs, setRef } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useGetProductByIdQuery, useUpdateSKUMutation } from '../features/products/productSlice';
import ProductGroupEditor from '../components/ProductGroupEditor';
import TemplateSelectionControl from '../components/TemplateSelectionControl';
import Loading from '../components/Loading';
import { SingleProductTypeSelect } from '../components/ProductAutocomplete';
import ProductMetadataEditor, { MetadataEditor } from '../components/ProductMetadataView';
import { useWindowSize } from '../helpers/window';
import TemplateSideTabs from '../components/TemplateSideTabs';
import ProductPhotoView from '../components/ProductPhotoView';
import { useGetSKUImagesQuery, useListSKUsQuery } from '../features/model/skuSlice';
import { SKUImageView } from '../components/SKUImageView';
import { useListToolProfilesQuery } from "../features/model/toolprofileSlice";

import { useCreateMetadataMutation, useDeleteMetadataMutation, useGetSKUMetadataQuery, useUpdateMetadataMutation } from '../features/products/metadataSlice';
import { useGetPurchaseOrderTemplateQuery, useUpdatePurchaseOrderDataMutation } from '../features/po/poSlice';
import { formatCurrency, maybeFormatCurrency } from '../helpers/strings';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDeleteSKUImageMutation } from '../features/model/skuimageSlice';
import { POOptionItem } from './po/Extras';
import { MetadataSelector } from '../components/MetadataSelector';
import { create } from '@mui/material/styles/createTransitions';

const CustomItemMetadataEditor = ({product}) => {
    const metadata = useGetSKUMetadataQuery(product.id);
    const profiles = useListToolProfilesQuery();
    const po = useGetPurchaseOrderTemplateQuery({type: "custom"});
    const [updateFormData, { isLoading: isUpdating, data: formTotalsData }] = useUpdatePurchaseOrderDataMutation();
    const [updateMetadata] = useUpdateMetadataMutation();
    const [createMetadata] = useCreateMetadataMutation();
    const [deleteMeta, {isLoading: isDeleting}] = useDeleteMetadataMutation();

    const [addingMeta, setAddingMeta] = useState(null);
    const [data, setData] = useState({});
    let [sheet, setSheet] = useState("");

    const effect = useEffect(() => {
        async function loadFromMetadata(meta) {
            // load sheet from metadata?
            var sheet_name = "";
            if("custom_sheet" in meta) {
                sheet_name = meta["custom_sheet"].svalue;
            } else {
                if("custom_sheet" in data) {
                    sheet_name = data["custom_sheet"];
                } else {
                    sheet_name = sheet;
                }
            }
            if (sheet_name !== sheet) await setSheet(sheet_name);
            const newData = Object.fromEntries(metadata.data.objects.filter((md) => md.key !== "custom_sheet").map(x => [x.key, x.svalue]));
            await setData(newData);
            await updateFormData({[sheet_name] : [newData]});
        }
        if(metadata) {
            if(metadata.data) {
                const meta = Object.fromEntries(metadata.data.objects.map(x => [x.key, x]));
                loadFromMetadata(meta);
            }
        }
    }, [sheet, metadata]);

    if (!metadata.data || !po.data || !profiles.data) return <Loading />;
    const meta = Object.fromEntries(metadata.data.objects.map(x => [x.key, x]));

    async function handleSave() {
        Object.keys(data).forEach(async (key) => {
            const value=data[key];
            if(key in meta) {
                let meta_obj = {...meta[key], svalue: value};
                await updateMetadata({skumetadata_id: meta_obj.id, data: meta_obj})
            } else {
                await createMetadata({SKU: product.id, key: key, svalue: value})
            }
        });
        // save selected sheet, too

        if("custom_sheet" in meta) {
            let meta_obj = {...meta["custom_sheet"], svalue: sheet};
            await updateMetadata({skumetadata_id: meta_obj.id, data: meta_obj});
        } else {
            await createMetadata({SKU: product.id, key: "custom_sheet", svalue: sheet});
        }
    }

    const tables = po.data.form_contents.filter((c) => c.type === "table");
    let custom_item_types = Object.fromEntries(tables.map(x => [x.key, x.label]));
    const sel_tables = tables.filter((t) => t.key === sheet);
    const selector = <FormControl sx={{marginBottom: 3, maxWidth: 600}}>
            <InputLabel id="customtypelabel" shrink>Custom Item Type</InputLabel>
            <Select labelId="customtypelabel" label="Custom Item Type" id="custom_item_type" value={sheet} onChange={(e) => {
                setSheet(e.target.value);
                updateFormData({[sheet]: [data]});
            }}>
                {Object.keys(custom_item_types).map((key) =>
                    <MenuItem key={key} value={key}>{custom_item_types[key]}</MenuItem>
                )}
            </Select>
        </FormControl>;
    if (sel_tables.length === 0) {
        return <Stack direction="column">
            <Typography key="nothing" sx={{marginBottom: 2}}>Please select an item type</Typography>
            {selector}
        </Stack>;
    }
    const table = sel_tables[0];

    const makeShortLabel = (label) => {
        return label.split("\n")[0].split(" (")[0];
    };
    
    function handleValueChange(key, value) {
        let newData = {...data};
        newData[key]=value;
        setData(newData);
        updateFormData({
            [sheet]: [newData]
        });
    }

    let autodata = {};
    // conditionally pre-init "auto/setup" fields
    table.fields.forEach((field) => {
        if(field.type === "auto") {
            if(formTotalsData) {
                if(sheet in formTotalsData) {
                    if(field.key in formTotalsData[sheet][0]) {
                        autodata[field.key] = formTotalsData[sheet][0][field.key];
                    }
                }
            }
        }
    })

    let got_keys = [];
    let extra_meta = [];
    if(metadata.data) {
        metadata.data.objects.forEach(meta => {
            got_keys.push(meta.key);
            if(!(table.fields.map((f) => f.key).includes(meta.key)))
                if(meta.key !== "custom_sheet")
                    extra_meta.push(meta);
        });
    }
    
    const profileSources = {
        toolprofiles_panel: profiles.data.objects.filter((p) => JSON.parse(p.usage).raised.enabled).map((p) => p.name)
    }

    const items = table.fields.map((field, index) => {
        var value = data[field.key];
        if(field.key in autodata && (!value || value === "")) {
            value = autodata[field.key];
        }
        return <Stack key={index} direction="column" sx={{maxWidth: 600}}>
            <Stack direction="row" sx={{marginBottom: 2, verticalAlign: "center"}}>
            <Typography sx={{width: 260, marginRight: 2, verticalAlign: "center"}}>{field.label}</Typography>
                {field.type === "choice" ? (
                <FormControl fullWidth>
                    <Select
                    sx={{
                        borderRadius: 0
                    }}
                    placeholder={makeShortLabel(field.label)}
                    value={data[field.key] || ""}
                    onChange={(e) => {
                        handleValueChange(
                        field.key,
                        e.target.value
                        );
                    }}>
                    {po.data.definitions[field.options].map(
                        (option, idx) => (
                        <MenuItem key={option.value} value={option.value}>
                            <POOptionItem field={field} option={option} />
                        </MenuItem>
                        )
                    )}
                    </Select>
                </FormControl>
                ) : field.type === "total" ? (
                <Typography sx={{ paddingLeft: 2 }} variant="h3">
                    {isUpdating
                    ? "..."
                    : formatCurrency(data[field.key] || "")}
                </Typography>
                ) : (field.type === "auto" && (!field.setup || (field.setup !== "manual"))) ? (
                <Typography sx={{ paddingLeft: 2 }} variant="h3">
                    {isUpdating
                    ? "..."
                    : maybeFormatCurrency(field.key, autodata[field.key] || "")}
                </Typography>
                ) : (
                <TextField
                    InputProps={{
                    sx: {
                        borderRadius: 0,
                        backgroundColor: "#ffffff",
                    },
                    readOnly: (field.type === "auto" && field.setup !== "manual"),
                    }}
                    placeholder={makeShortLabel(field.label)}
                    type={
                    field.type === "float" ? "number" : "text"
                    }
                    value={value}
                    onChange={(e) => {
                    handleValueChange(
                        field.key,
                        e.target.value
                    );
                    }}
                    required={!field.nullable}
                />
                )}
            {
                field.setup === "manual" && autodata[field.key] && autodata[field.key] !== data[field.key] ? 
                    <Button onClick={() => {
                        handleValueChange(field.key, autodata[field.key]);
                    }}>Reset to {`${autodata[field.key]}`}</Button>
                    : <></>
            }
        </Stack></Stack>
    });
    const extra_items = extra_meta.map((em) => {
        return <Stack direction="row" spacing={1} sx={{marginBottom: 2}}>
            <Typography sx={{width: 168, marginRight: 2, verticalAlign: "center"}}>{em.key}</Typography>
            <MetadataEditor key={em.key} metadata={em} customSources={profileSources} />
        </Stack>;
    });

    return <Stack direction="column">
            {selector}
        <Stack direction="column">
            {items}
            {extra_items}
            <Stack direction="row" spacing={1} sx={{marginBottom: 1, marginTop: 4}}>
            <MetadataSelector exclude={got_keys} metaSet={"lcc_custom"} onSelect={(value) => {
                setAddingMeta(value);
            }} />
            <Button variant="contained" onClick={() => {
                let newMetadata = {
                    SKU: product.id,
                    key: addingMeta.key,
                    svalue: addingMeta.defaultString,
                    ivalue: addingMeta.defaultInt,
                    fvalue: addingMeta.defaultFloat
                }
                createMetadata(newMetadata);
                setAddingMeta(null);
            }}>Add</Button>

        </Stack>
        <ButtonGroup>
                <Button variant="contained" onClick={handleSave}>Save</Button>
            </ButtonGroup>

        </Stack>
    </Stack>;
}

const CustomItemEditView = () => {
    let params = useParams();

    const default_state = {
        "id": 0,
        "name": "",
        "code": "",
        "item_type": "single",
    }

    let [state, setState] = useState(default_state);
    let [origState, setOrigState] = useState(default_state);

    const [refreshIdx, setRefreshIdx] = useState(0);

    const {data: product, error, isLoading } = useGetProductByIdQuery(params.objectId)
    const {data: skuimages, error: skuimagesError, isLoading: skuimagesIsLoading} = useGetSKUImagesQuery(product ? product.id : undefined, {skip: !product})

    const [updateSKU, {isLoadingUpdate: isUpdating }] = useUpdateSKUMutation();
    const [deleteSKUImage] = useDeleteSKUImageMutation();
    const windowSize = useWindowSize();

    if (isLoading || skuimagesIsLoading || !skuimages) {
        return <Loading />;
    }
    if (error || skuimagesError) {
        return <div>Error: {error}</div>
    }

    if(state.id !== product.id) {
        setState(product);
        setOrigState(product);
    }

    const handleSave = (event) => {
        updateSKU({ sku_id: state.id, data: state })
        setOrigState(state);
    }
    const handleRevert = (event) => {
        setState({ ...origState });
    }

    function triggerRefresh() {
        setRefreshIdx(refreshIdx + 1);
    }

    let product_images = []
    let imgWidth = 400;
    if (windowSize.width > 800) {
        imgWidth = 1200;
    } else if (windowSize.width > 400) {
        imgWidth = 800;
    }

    var next_purpose = "sample1";
    var custom_counter = 1;
    if(skuimages) {
        skuimages.objects.forEach((skuimage) => {
            product_images.push(<Stack direction="column">
                <SKUImageView skuimage={skuimage} key={skuimage.id} product={product} purpose={skuimage.purpose}/>
                <Button onClick={() => {
                    deleteSKUImage(skuimage.id);
                }}><DeleteIcon /></Button>
                </Stack>
            );
        })
        while(true) {
            let found = skuimages.objects.filter((s) => s.purpose === next_purpose).length > 0;
            if(found) {
                custom_counter += 1;
                next_purpose = `sample${custom_counter}`;
            } else {
                break;
            }
        }
    }

    return <Stack sx={{marginTop: 2, marginBottom: 4}} spacing={2}>
        <ButtonGroup>
            <Button component={Link} to={`/customitems`}>Back</Button>
        </ButtonGroup>

        <h2>{state.name}</h2>
        <TextField label="Name" value={state.name} onChange={(event) => {setState({...state, name: event.target.value});}} />
        <TextField label="SKU" inputProps={{ style: { fontFamily: "Courier" } }} value={state.code} onChange={(event) => {setState({...state, code: event.target.value})}} />

        <ButtonGroup>
            <Button onClick={handleRevert}>Revert</Button>
            <Button variant="contained" onClick={handleSave}>Save</Button>
        </ButtonGroup>
        <Card><Paper variant="outlined" sx={{padding: 1}}>
            <Typography variant="h7">Product photos</Typography>
        <Stack direction="row" spacing={2} sx={{marginTop: 1, overflow: "auto"}}>
            <SKUImageView skuimage={null} product={product} showUpload={true} purpose={next_purpose} />
            {product_images}
        </Stack>
        </Paper></Card>
        <Card><Paper variant="outlined" sx={{padding: 1}}>
            <CustomItemMetadataEditor product={product} />
        </Paper></Card>
    </Stack>
}

const CustomItemImagePreviews = ({item}) => {
    const {data: skuimages, error, isLoading} = useGetSKUImagesQuery(item.id);
    if(isLoading) return <Loading />;
    if(error) return <div>{error.error}</div>;
    var product_images = [];
    if(skuimages) {
        skuimages.objects.forEach((skuimage) => {
            product_images.push(
                <SKUImageView skuimage={skuimage} key={skuimage.id} product={item} purpose={skuimage.purpose}/>
            );
        })
    };
    return <>
        <Typography variant="h7">Product photos</Typography>
            <Stack direction="row" spacing={2} sx={{marginTop: 1, overflow: "auto"}}>
                {product_images}
            </Stack>
        </>;
    

}

const CustomItemPreviewView = ({code}) => {
    const skuQuery = useListSKUsQuery({code: code});
    
    if(skuQuery.isLoading) return <Loading />;
    if(!skuQuery.data) return <></>;
    if(skuQuery.data.objects.length === 0) return <Typography>Item not found.</Typography>

    const item = skuQuery.data.objects[0];
    return <Stack sx={{margin: 4}} spacing={2}>
    <Typography variant="h2">{item.name}</Typography>
    <Typography variant="h3">{item.code}</Typography>
    <Card><Paper variant="outlined" sx={{padding: 1}}>
        <CustomItemImagePreviews item={item} />
    </Paper></Card>
    </Stack>
}

export {CustomItemPreviewView};
export default CustomItemEditView;
